// Redeploy trigger: 2026-06-25
'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface Stat {
  value: string;
  label: string;
}

interface CaseStudy {
  id: string;
  category: string;
  subtitle: string;
  title: string;
  description: string;
  stats: Stat[];
  quote: string | null;
  quoteSource: string | null;
  pages: number;
  language: string;
  pageUrl: string;
}

const caseStudies: CaseStudy[] = [
{
  id: 'consumer-electronics',
  category: 'Consumer Electronics',
  subtitle: 'Annual and launch-based delivery program',
  title: 'Global Content Localization for Leading Consumer Electronics Brands',
  description: 'Global consumer electronics launches required multilingual content across product, marketing, support, and compliance materials while keeping tone, terminology, and release timing aligned across markets.',
  stats: [
  { value: 'Millions+', label: 'Annual Volume' },
  { value: '30+', label: 'Languages' },
  { value: '100%', label: 'On-Time Delivery' },
  { value: 'Hundreds', label: 'Launch Programs' }],
  quote: '"Rapid turnaround with consistent quality across all markets."',
  quoteSource: 'Client feedback, Leading consumer electronics brands',
  pages: 5,
  language: 'English',
  pageUrl: '/case-studies/consumer-electronics'
},
{
  id: 'life-sciences-elearning',
  category: 'Life Sciences',
  subtitle: 'AI-led production with human review',
  title: 'Transforming Life Science eLearning with AI-Based Multilingual Localization',
  description: 'A regulated eLearning program needed translation and professionally narrated training content in multiple languages while compressing timelines and controlling cost without sacrificing scientific accuracy.',
  stats: [
  { value: '60% Faster', label: 'Timeline Reduction' },
  { value: '50% Savings', label: 'Cost Reduction' }],
  quote: null,
  quoteSource: null,
  pages: 6,
  language: 'English',
  pageUrl: '/case-studies/life-sciences-elearning'
},
{
  id: 'legal-translation',
  category: 'Legal',
  subtitle: 'Large-scale document engineering',
  title: 'Large-Scale Legal Translation and Document Engineering',
  description: 'A major legal matter required more than one million words of Chinese-to-English legal content converted from complex PDFs into accurate, editable Word files under an extreme deadline.',
  stats: [
  { value: '1M+', label: 'Word Volume' },
  { value: '7 Days', label: 'Turnaround' }],
  quote: null,
  quoteSource: null,
  pages: 4,
  language: 'English',
  pageUrl: '/case-studies/legal-translation'
},
{
  id: 'ecommerce-localization',
  category: 'E-Commerce',
  subtitle: 'High-volume multilingual localization',
  title: 'Multilingual Localization for eCommerce',
  description: 'The client needed high-volume eCommerce localization across 20+ languages with tight deadlines, budget control, culturally accurate output, and repeatable quality standards for each market.',
  stats: [
  { value: '99% Accepted', label: 'First Delivery' },
  { value: '99%', label: 'On-Time Delivery' }],
  quote: null,
  quoteSource: null,
  pages: 5,
  language: 'English',
  pageUrl: '/case-studies/ecommerce-localization'
},
{
  id: 'dataforce-transcription',
  category: 'AI Data',
  subtitle: 'Multilingual AI data operations',
  title: 'DataForce Multilingual Transcription Support',
  description: 'A large AI data program required multilingual review and correction of AI-generated transcriptions across a broad language set, using trained reviewers, client tools, and dependable QA governance.',
  stats: [
  { value: '25', label: 'Languages' },
  { value: '10 per language', label: 'Reviewer Team' }],
  quote: null,
  quoteSource: null,
  pages: 4,
  language: 'English',
  pageUrl: '/case-studies/dataforce-transcription'
},
{
  id: 'sify-voiceover',
  category: 'Voiceover',
  subtitle: 'eLearning multilingual voiceover',
  title: 'Sify Digital Multilingual eLearning Voiceover',
  description: 'Sify Digital needed multilingual voiceover for global eLearning courses with tight delivery windows, high linguistic accuracy, and consistent audio quality across a wide set of languages and voice profiles.',
  stats: [
  { value: '18', label: 'Languages' },
  { value: '97% Accepted', label: 'First Delivery' }],
  quote: null,
  quoteSource: null,
  pages: 5,
  language: 'English',
  pageUrl: '/case-studies/sify-voiceover'
},
{
  id: 'voice-data-ai',
  category: 'AI Data',
  subtitle: 'Voice dataset for speech model training',
  title: 'Voice Data for AI Training for a Synthetic Speech Technology Company',
  description: 'A voice AI company needed high-fidelity multilingual recordings that met strict technical requirements, accent expectations, and ethical consent standards for speech-model training.',
  stats: [
  { value: '20', label: 'Languages' },
  { value: 'Auditable consent', label: 'Consent Controls' }],
  quote: null,
  quoteSource: null,
  pages: 6,
  language: 'English',
  pageUrl: '/case-studies/voice-data-ai'
}];

const categories = ['All Industries', 'Consumer Electronics', 'Life Sciences', 'Legal', 'E-Commerce', 'AI Data', 'Voiceover'];

export default function CaseStudiesPage() {
  const [activeCategory, setActiveCategory] = useState('All Industries');

  const filteredStudies = activeCategory === 'All Industries' ?
  caseStudies :
  caseStudies.filter((cs) => cs.category === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Case studies background showing global localization operations"
            className="w-full h-full object-cover opacity-30"
            src="/assets/images/rocket-migrated/rocket_gen_img_12caf9859-1782361606788.png" />
          
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-background/70 to-background" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
            Proven Results
          </span>
          <h1 className="font-display font-bold text-5xl lg:text-6xl text-silver-text leading-tight mb-6 max-w-3xl">
            Case Studies
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed">Discover how Into23 supports multilingual launches, regulated content, and Data+ programs across APAC and global markets with measurable business outcomes.

          </p>
        </div>
      </section>

      {/* ===== FILTER + GRID ===== */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeCategory === cat ?
              'bg-cobalt text-white' : 'bg-graphite border border-steel/30 text-silver-muted hover:text-silver-text hover:border-cobalt/30'}`
              }>
              
                {cat}
              </button>
            )}
          </div>

          {/* Results count */}
          <p className="text-sm text-silver-muted mb-8">
            Showing {filteredStudies.length} {filteredStudies.length === 1 ? 'case study' : 'case studies'}
            {activeCategory !== 'All Industries' && ` in ${activeCategory}`}
          </p>

          {/* Case Studies Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudies.map((cs) =>
            <div key={cs.id} className="glow-card rounded-xl p-7 flex flex-col group">
                {/* Category badge */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">
                    {cs.category}
                  </span>
                  <span className="text-xs text-silver-muted">{cs.subtitle}</span>
                </div>

                {/* Title */}
                <h3 className="font-display font-semibold text-lg text-silver-text mb-3 group-hover:text-cobalt-glow transition-colors leading-snug">
                  {cs.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-silver-muted leading-relaxed mb-5 flex-1 line-clamp-3">
                  {cs.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-steel/20 mb-5">
                  {cs.stats.slice(0, 2).map((stat) =>
                <div key={stat.label}>
                      <div className="text-sm font-semibold text-cobalt-glow">{stat.value}</div>
                      <div className="text-xs text-silver-muted">{stat.label}</div>
                    </div>
                )}
                </div>

                {/* Quote if present */}
                {cs.quote &&
              <blockquote className="mb-5 border-l-2 border-cobalt/40 pl-4">
                    <p className="text-xs text-silver-muted italic leading-relaxed mb-1">{cs.quote}</p>
                    <cite className="text-xs text-cobalt-glow not-italic">{cs.quoteSource}</cite>
                  </blockquote>
              }

                {/* Read Case Study Link */}
                <Link
                href={cs.pageUrl}
                className="mt-auto flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-cobalt/30 text-cobalt-glow text-sm font-medium hover:bg-cobalt/10 hover:border-cobalt/60 transition-all">
                
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                  </svg>
                  Read Case Study
                </Link>
              </div>
            )}
          </div>

          {/* Empty state */}
          {filteredStudies.length === 0 &&
          <div className="text-center py-20">
              <p className="text-silver-muted text-lg">No case studies found for this category.</p>
              <button
              onClick={() => setActiveCategory('All Industries')}
              className="mt-4 text-cobalt-glow hover:text-cobalt text-sm font-medium transition-colors">
              
                View all case studies
              </button>
            </div>
          }
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="glow-card rounded-2xl p-10 lg:p-16 text-center">
            <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
              Work With Us
            </span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-silver-text mb-4">
              Ready to Scale Your Language Operations?
            </h2>
            <p className="text-silver-muted max-w-xl mx-auto mb-8 leading-relaxed">
              Whether you need AI translation at scale, LLM evaluation, or a complete enterprise language program - Into23 has the technology and expertise to deliver.
            </p>
            <Link href="/get-a-quote">
              <span className="inline-flex items-center gap-2 px-8 py-4 bg-cobalt hover:bg-cobalt-glow text-white font-semibold rounded-lg transition-all hover:shadow-[0_0_20px_rgba(43,98,239,0.3)]">
                Get a Quote
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

    </div>);

}